import ErrorUtil from '@/util/ErrorUtil';
import StorageUtil, { LOCALSTORAGE_STORAGE } from '@/util/StorageUtil';
import SystemUtil from '@/util/SystemUtil';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import Store from './Store';
import { db, FotoMagistrado } from './db';

/**
 * Classe utilizada para guardar e manipular o estado da aplicação.
 * Deve-se criar uma para cada contexto ou entidade.
 */
class FotoMagistradoStore extends Store {
    fotosDisponiveisEmMemoria = new Map();

    // Controla requisições em andamento para evitar duplicidade
    requisicoesEmAndamento = new Map();

    constructor(props) {
        super(props);

        makeObservable(this, {
            fotosDisponiveisEmMemoria: observable.shallow,
            objetoLocalStorage: computed,
            getFotoMagistradoStorage: action
        });
    }

    get storageType() {
        return LOCALSTORAGE_STORAGE;
    }

    inicializar() {
        super.inicializar();

        this.atualizarCacheLocal().catch((error) => {
            console.error('Falha ao atualizar cache de fotos de magistrados', error);
        });
    }

    get objetoLocalStorage() {
        return {
            nomeStore: 'FotoMagistradoStore'
        };
    }

    async getFotoMagistradoStorage(cpf) {
        const storage = await StorageUtil.getObject(`${this.objetoLocalStorage.nomeStore}-${cpf}`);

        if (storage && typeof storage === 'object' && storage['fotoMagistrado']) {
            return storage['fotoMagistrado'];
        }

        return '';
    }

    async consultarMagistradoComposicao(magistradoComposicao) {
        const cpf = magistradoComposicao?.magistrado?.cpf;
        const tipoImg = magistradoComposicao?.imagem?.tipo;

        if (!cpf) {
            return null;
        }

        const fotoMemoria = this.fotosDisponiveisEmMemoria.get(cpf);

        if (fotoMemoria && (!tipoImg || fotoMemoria.imgContentType)) {
            return fotoMemoria;
        }

        if (this.requisicoesEmAndamento.has(cpf)) {
            return this.requisicoesEmAndamento.get(cpf);
        }

        const promessa = this.consultarPeloCpf(cpf, false, tipoImg);

        this.requisicoesEmAndamento.set(cpf, promessa);

        try {
            const foto = await promessa;

            runInAction(() => {
                const atual = this.fotosDisponiveisEmMemoria.get(cpf);

                if (!atual || atual.imgBase64 !== foto?.imgBase64) {
                    this.fotosDisponiveisEmMemoria.set(cpf, foto);
                }
            });

            return foto;
        } finally {
            this.requisicoesEmAndamento.delete(cpf);
        }
    }

    carregarFotosPelaComposicaoProcesso(composicao) {
        const promisesMap = new Map();

        composicao.forEach((comp) => {
            const promessa = this.consultarMagistradoComposicao(comp);
            promisesMap.set(comp.magistrado.cpf, promessa);
        });

        return Promise.all(Array.from(promisesMap.values()));
    }

    async consultarPeloCpf(cpf, ignorarCacheLocal = false, tipoImg = undefined) {
        let fotoBin = null;
        let fotoObj = null;

        if (!cpf) {
            throw new ReferenceError(
                `Parâmetro inválido para \`FotoMagistradoStore.consultar\`: \`cpf\` não pode ser nulo.`
            );
        }

        if (ignorarCacheLocal) {
            fotoBin = await this.fotoMagistradoService.recuperarFoto(cpf);
            fotoObj = await this.armazenarNoCacheLocal(cpf, fotoBin, tipoImg);
        } else {
            const itemCache = await this.consultarCacheLocal(cpf);

            if (itemCache?.idMagistrado && itemCache.imgBase64) {
                if (tipoImg && !itemCache.imgContentType) {
                    fotoObj = await this.armazenarNoCacheLocal(itemCache.idMagistrado, itemCache.imgBase64, tipoImg);
                } else {
                    fotoObj = itemCache;
                }
            } else {
                fotoBin = await this.fotoMagistradoService.recuperarFoto(cpf);
                fotoObj = await this.armazenarNoCacheLocal(cpf, fotoBin, tipoImg);
            }
        }

        return fotoObj;
    }

    async carregarFotoMagistrado(sigla, composicao) {
        if (!sigla || !composicao || !Array.isArray(composicao) || composicao.length === 0) {
            return null;
        }

        const magistradoComposicao = composicao.find((comp) => comp.magistrado?.sigla === sigla);

        if (!magistradoComposicao) {
            return null;
        }

        return this.consultarMagistradoComposicao(magistradoComposicao);
    }

    async armazenarNoCacheLocal(idMagistrado, imgBin, tipoImg) {
        if (!imgBin) {
            return null;
        }

        const imgBlob = new Blob([imgBin], { type: 'image/jpeg' });
        const imgBase64 = await SystemUtil.blobToBase64(imgBlob);

        if (!tipoImg) {
            const item = await this.consultarCacheLocal(idMagistrado);

            if (item) {
                tipoImg = item.imgContentType;
            }
        }

        const tupla = new FotoMagistrado(idMagistrado, imgBase64, tipoImg);

        try {
            await db.table('fotoMagistrado').put(tupla);

            console.log(`Foto do magistrado "${tupla.idMagistrado}" armazenada no cache local com sucesso.`);
        } catch (error) {
            console.error(`Falha ao gravar a foto no cache local: ${ErrorUtil.getMensagemErro(error)}`, {
                cause: error
            });
        }

        return tupla;
    }

    async consultarCacheLocal(idMagistrado) {
        let item = null;

        try {
            item = await db
                .table('fotoMagistrado')
                .filter((magistrado) => magistrado.idMagistrado === idMagistrado)
                .first();
        } catch (error) {
            throw new Error(
                `Falha ao consultar o cache local de fotos de magistrados: ${ErrorUtil.getMensagemErro(error)}`,
                { cause: error }
            );
        }

        return item;
    }

    static async limparCacheLocal() {
        try {
            await db.table('fotoMagistrado').clear();
        } catch (error) {
            throw new Error('Falha ao limpar o cache de fotos de magistrados', { cause: error });
        }
    }

    async atualizarCacheLocal() {
        const itens = await db.table('fotoMagistrado').toArray();

        console.log('Atualizando cache de fotos de magistrados...');

        const promises = itens.map((item) => this.consultarPeloCpf(item.idMagistrado, true).catch(() => null));

        return Promise.all(promises);
    }
}

export default FotoMagistradoStore;
